import { useEffect, useState, useRef } from "react";
import { Swords } from "lucide-react";

// KiaCounter — rolling 60-second window of unit deaths in the scene.
// - Green count = bulls killed (they died so bears won that skirmish).
// - Red count   = bears killed.
// - Reset key changes on pair switch → all counters cleared.
//
// The `killTrackerRef` is a { current: Array<{side, ts}> } mutated by
// Battlefield3D's onKill callback. We poll it every ~500ms, evict entries
// older than 60s, and re-render the counts.
export default function KiaCounter({ killTrackerRef, resetKey, compact = false }) {
  const [counts, setCounts] = useState({ bulls: 0, bears: 0 });
  const resetKeyRef = useRef(resetKey);

  useEffect(() => {
    if (resetKeyRef.current !== resetKey) {
      if (killTrackerRef?.current) killTrackerRef.current.length = 0;
      resetKeyRef.current = resetKey;
      setCounts({ bulls: 0, bears: 0 });
    }
  }, [resetKey, killTrackerRef]);

  useEffect(() => {
    const id = setInterval(() => {
      const arr = killTrackerRef?.current;
      if (!arr) return;
      const cutoff = performance.now() - 60_000;
      let removeUpTo = 0;
      while (removeUpTo < arr.length && arr[removeUpTo].ts < cutoff) removeUpTo++;
      if (removeUpTo > 0) arr.splice(0, removeUpTo);
      let bulls = 0;
      let bears = 0;
      for (let i = 0; i < arr.length; i++) {
        if (arr[i].side === "bull") bulls++;
        else bears++;
      }
      setCounts((prev) => (prev.bulls === bulls && prev.bears === bears ? prev : { bulls, bears }));
    }, 500);
    return () => clearInterval(id);
  }, [killTrackerRef]);

  if (compact) {
    return (
      <div
        className="cb-hud-chip flex items-center gap-2 px-2.5 py-1"
        data-testid="kia-counter"
      >
        <Swords className="w-3 h-3 text-slate-400" />
        <span
          className="font-mono-tac text-[10px] tabular-nums text-[#00FFA3]"
          data-testid="kia-bulls-down"
        >
          {counts.bulls}
        </span>
        <span className="text-slate-600">/</span>
        <span
          className="font-mono-tac text-[10px] tabular-nums text-[#FF2A2A]"
          data-testid="kia-bears-down"
        >
          {counts.bears}
        </span>
      </div>
    );
  }

  return (
    <div
      className="cb-hud-chip flex flex-col gap-1 px-3 py-2 pointer-events-auto"
      data-testid="kia-counter"
    >
      <div className="flex items-center gap-3 font-mono-tac text-[11px] tracking-[0.16em]">
        <Swords className="w-3.5 h-3.5 text-slate-400" />
        <span className="flex items-baseline gap-1.5">
          <span className="text-[9px] uppercase tracking-[0.28em] text-slate-400">Bulls down</span>
          <span
            className="text-[13px] tabular-nums font-semibold text-[#00FFA3]"
            data-testid="kia-bulls-down"
          >
            {counts.bulls}
          </span>
        </span>
        <span className="text-slate-700">|</span>
        <span className="flex items-baseline gap-1.5">
          <span className="text-[9px] uppercase tracking-[0.28em] text-slate-400">Bears down</span>
          <span
            className="text-[13px] tabular-nums font-semibold text-[#FF2A2A]"
            data-testid="kia-bears-down"
          >
            {counts.bears}
          </span>
        </span>
      </div>
      <span className="font-mono-tac text-[9px] uppercase tracking-[0.28em] text-slate-500">
        (rolling 60s)
      </span>
    </div>
  );
}
